// =====================================================================================
// EditModeContext - Manages decoration edit mode state and operations
// =====================================================================================
// Thin orchestrator that composes useEditModeSocket, useEditModePhaser,
// useEditModeHistory, and useDecorationPalette into a single context provider.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGameInstance } from './GameInstanceContext.js';
import { useEditModeSocket } from '../hooks/useEditModeSocket.js';
import { useEditModePhaser } from '../hooks/useEditModePhaser.js';
import { useEditModeHistory } from '../hooks/useEditModeHistory.js';
import { useDecorationPalette } from '../hooks/useDecorationPalette.js';
import type { TilemapObject, DecorationChange } from '@shared/schemas/decorations.schema.js';
import type { EditMode, DecorationItem } from './editMode.types.js';
import type { RemoveUndoData } from '../hooks/useEditModePhaser.js';
import type { SpriteState } from '../hooks/useEditModeHistory.js';

/**
 * React Context for decoration edit mode functionality
 * Imported from editModeContext.ts -- keep context object in a .ts file
 * so this .tsx file exports only components (required for React Fast Refresh).
 */
import { EditModeContext } from './editModeContext.js';

/**
 * Edit Mode Provider component that manages decoration editing state
 * Handles WebSocket integration and staged changes for decoration operations
 */
export function EditModeProvider({ children }: { children: React.ReactNode }) {
  const { getGameScene } = useGameInstance();

  // ── Core state ──────────────────────────────────────────────────────────────
  const [isActive, setIsActive] = useState(false);
  const [mode, setMode] = useState<EditMode>('select');
  const [selectedDecoration, setSelectedDecoration] = useState<DecorationItem | null>(null);
  const [selectedPlacedDecorationId, setSelectedPlacedDecorationId] = useState<string | null>(null);

  // Staged changes tracking
  const [stagedChanges, setStagedChanges] = useState<DecorationChange[]>([]);
  const localDecorationIds = useRef<Set<number>>(new Set());

  // Keep a ref in sync for synchronous reads inside callbacks
  const stagedChangesRef = useRef<DecorationChange[]>(stagedChanges);
  useEffect(() => { stagedChangesRef.current = stagedChanges; }, [stagedChanges]);

  // Computed state
  const hasUnsavedChanges = stagedChanges.length > 0;

  // ── Composed hooks ──────────────────────────────────────────────────────────

  // WebSocket communication (commit, remote updates, loading/error state)
  const { commitChanges, isLoading, error, setError } = useEditModeSocket(
    stagedChanges,
    setStagedChanges,
    localDecorationIds,
    getGameScene
  );

  // Decoration palette (available decorations, categories, filtering)
  const {
    availableDecorations,
    categories,
    selectedCategory,
    setSelectedCategory,
    loadDecorationsFromTilesets,
    filteredDecorations,
  } = useDecorationPalette();

  // Undo/redo history
  const {
    pushAction,
    undo,
    redo,
    canUndo,
    canRedo,
    resetHistory,
    historyVersion,
  } = useEditModeHistory(getGameScene, setStagedChanges, localDecorationIds);

  // ── Local change operations ─────────────────────────────────────────────────

  // Stage a decoration change
  const stageChange = useCallback((change: DecorationChange) => {
    setStagedChanges(prev => [...prev, change]);
  }, []);

  // Add decoration at position (with history tracking)
  const addDecoration = useCallback((x: number, y: number, objectId?: number) => {
    if (!selectedDecoration) {
      return;
    }

    const newObject: TilemapObject = {
      id: objectId,
      gid: selectedDecoration.gid,
      x,
      y,
      width: selectedDecoration.width || 32,
      height: selectedDecoration.height || 32,
      visible: true,
      properties: [
        { name: 'name', type: 'string', value: selectedDecoration.name },
        { name: 'category', type: 'string', value: selectedDecoration.category },
        { name: 'imagePath', type: 'string', value: selectedDecoration.imagePath || '' },
        { name: 'tilesetName', type: 'string', value: selectedDecoration.tilesetName || '' }
      ]
    };

    // Include objectgroup if it exists (collision shapes, snap points, etc.)
    if (selectedDecoration.objectgroup) {
      (newObject as any).objectgroup = selectedDecoration.objectgroup;
    }

    const change: DecorationChange = {
      type: 'add',
      object: newObject
    };

    stageChange(change);

    // Track this as a locally-added decoration
    if (objectId !== undefined) {
      localDecorationIds.current.add(objectId);

      // Record undo action
      // NOTE: DecorationManager stores decorations keyed by config.id (e.g. "42"),
      // NOT by the "decoration_42" string that addDecoration() returns.
      const decorationId = String(objectId);
      const scene = getGameScene();
      const config = scene?.systems?.decorations?.getDecoration(decorationId)?.config;

      pushAction({
        type: 'add',
        objectId,
        decorationId,
        addedEntry: change,
        decorationConfig: config ? { ...config } : undefined,
      });
    }
  }, [selectedDecoration, stageChange, getGameScene, pushAction]);

  // Remove decoration by object ID (with history tracking)
  const removeDecoration = useCallback((objectId: number, undoData?: RemoveUndoData) => {
    const wasLocal = localDecorationIds.current.has(objectId);
    const decorationId = String(objectId);

    // Capture the 'add' entry before it gets removed (for undo of local removal)
    let removedAddEntry: DecorationChange | undefined;
    if (wasLocal) {
      removedAddEntry = stagedChangesRef.current.find(
        c => c.type === 'add' && c.object?.id === objectId
      );
      if (removedAddEntry) {
        removedAddEntry = { ...removedAddEntry };
      }
    }

    // Perform the actual removal
    if (wasLocal) {
      // Remove the 'add' change instead of adding a 'remove'
      setStagedChanges(prev => prev.filter(change =>
        !(change.type === 'add' && change.object?.id === objectId)
      ));
      localDecorationIds.current.delete(objectId);
    } else {
      const change: DecorationChange = {
        type: 'remove',
        objectId
      };
      stageChange(change);
    }

    // Record undo action
    pushAction({
      type: 'remove',
      objectId,
      decorationId,
      wasLocal,
      removedAddEntry,
      decorationConfig: undoData?.decorationConfig ? { ...undoData.decorationConfig } : undefined,
      removedSpriteState: undoData?.spriteState ? { ...undoData.spriteState } : undefined,
    });
  }, [stageChange, pushAction]);

  // Update decoration properties (with history tracking)
  const updateDecoration = useCallback((
    objectId: number,
    oldObject: TilemapObject,
    newObject: TilemapObject,
    preSpriteState?: SpriteState
  ) => {
    const decorationId = String(objectId);

    // Capture previous staged entry for undo
    const existingEntry = stagedChangesRef.current.find(
      c => c.type === 'update' && c.objectId === objectId
    );
    const prevUpdateEntry = existingEntry ? { ...existingEntry } : null;

    // Compute previous sprite state if not provided (fallback to existing entry or config)
    let resolvedPrevState = preSpriteState;
    if (!resolvedPrevState) {
      if (existingEntry?.object) {
        const depthProp = existingEntry.object.properties?.find(p => p.name === 'depth');
        resolvedPrevState = {
          x: existingEntry.object.x + (existingEntry.object.width / 2),
          y: existingEntry.object.y,
          depth: depthProp ? Number(depthProp.value) : 0
        };
      } else {
        // First update: use oldObject (config position) as previous
        resolvedPrevState = {
          x: oldObject.x + (oldObject.width / 2),
          y: oldObject.y,
          depth: 0
        };
      }
    }

    // Compute next sprite state from newObject
    const newDepthProp = newObject.properties?.find(p => p.name === 'depth');
    const nextSpriteState: SpriteState = {
      x: newObject.x + (newObject.width / 2),
      y: newObject.y,
      depth: newDepthProp ? Number(newDepthProp.value) : resolvedPrevState.depth
    };

    // Compute next staged entry (mirrors the merge logic below)
    const nextUpdateEntry: DecorationChange = existingEntry
      ? { type: 'update', objectId, oldObject: existingEntry.oldObject!, object: newObject }
      : { type: 'update', objectId, oldObject, object: newObject };

    // Perform the actual staged changes update (merges with existing for same objectId)
    setStagedChanges(prev => {
      const existingIdx = prev.findIndex(c => c.type === 'update' && c.objectId === objectId);
      if (existingIdx !== -1) {
        // Keep the original oldObject (for backend matching), use latest newObject
        const updated = [...prev];
        updated[existingIdx] = {
          type: 'update',
          objectId,
          oldObject: prev[existingIdx].oldObject,
          object: newObject
        };
        return updated;
      }
      return [...prev, { type: 'update', objectId, oldObject, object: newObject }];
    });

    // Record undo action
    pushAction({
      type: 'update',
      objectId,
      decorationId,
      prevUpdateEntry,
      nextUpdateEntry,
      prevSpriteState: resolvedPrevState,
      nextSpriteState,
    });
  }, [pushAction]);

  // Phaser scene bridge (selection, mode changes, depth, event listeners)
  const {
    selectDecoration,
    selectPlacedDecoration,
    changeMode,
    adjustDecorationDepth,
  } = useEditModePhaser(
    isActive,
    mode,
    selectedDecoration,
    setMode,
    setSelectedDecoration,
    setSelectedPlacedDecorationId,
    addDecoration,
    removeDecoration,
    updateDecoration
  );

  // ── Edit mode lifecycle ─────────────────────────────────────────────────────

  // Clear error when entering edit mode
  useEffect(() => {
    if (isActive && error) {
      setError(null);
    }
  }, [isActive, error, setError]);

  // Rollback all staged changes
  const rollbackChanges = useCallback(() => {
    const scene = getGameScene();
    if (scene && scene.systems?.decorations) {
      scene.systems.decorations.clearTrackedChanges();
    }

    setStagedChanges([]);
    localDecorationIds.current = new Set();
    setError(null);
    resetHistory();
  }, [getGameScene, setError, resetHistory]);

  // Enter edit mode
  const enterEditMode = useCallback(async () => {
    setIsActive(true);
    setMode('select');
    setSelectedDecoration(null);
    setStagedChanges([]);
    setError(null);
    resetHistory();

    // Preload palette textures for ghost previews
    const scene = getGameScene();
    if (scene) {
      const decorationSystem = (scene as any).systems?.decorations;

      if (decorationSystem && availableDecorations.length > 0) {
        const tilemapData = scene.cache.json.get('main_island_raw');
        if (tilemapData?.tilesets) {
          try {
            await decorationSystem.preloadPaletteTextures(tilemapData.tilesets);
          } catch (err) {
            console.warn('[EditMode] Failed to preload palette textures:', err);
          }
        }
      }
    }
  }, [getGameScene, availableDecorations.length, setError, resetHistory]);

  // Exit edit mode
  const exitEditMode = useCallback(() => {
    if (stagedChanges.length > 0) {
      rollbackChanges();
    }

    setIsActive(false);
    setMode('select');
    setSelectedDecoration(null);
    setSelectedPlacedDecorationId(null);
    setStagedChanges([]);
    setError(null);
    resetHistory();
    // Don't reset decorationsLoadedRef to prevent reload loops
  }, [stagedChanges.length, rollbackChanges, setError, resetHistory]);

  // ── Context value ───────────────────────────────────────────────────────────

  const value = React.useMemo(() => ({
    // State
    isActive,
    mode,
    selectedDecoration,
    selectedPlacedDecorationId,
    stagedChanges,
    hasUnsavedChanges,
    availableDecorations: filteredDecorations,
    categories,
    selectedCategory,
    isLoading,
    error,

    // Methods
    enterEditMode,
    exitEditMode,
    selectDecoration,
    selectPlacedDecoration,
    setMode: changeMode,
    setSelectedCategory,
    stageChange,
    commitChanges,
    rollbackChanges,
    addDecoration,
    removeDecoration,
    updateDecoration,
    adjustDecorationDepth,
    loadDecorationsFromTilesets,

    // Undo/redo
    undo,
    redo,
    canUndo,
    canRedo,
  }), [
    isActive,
    mode,
    selectedDecoration,
    selectedPlacedDecorationId,
    stagedChanges,
    hasUnsavedChanges,
    filteredDecorations,
    categories,
    selectedCategory,
    isLoading,
    error,
    enterEditMode,
    exitEditMode,
    selectDecoration,
    selectPlacedDecoration,
    changeMode,
    setSelectedCategory,
    stageChange,
    commitChanges,
    rollbackChanges,
    addDecoration,
    removeDecoration,
    updateDecoration,
    adjustDecorationDepth,
    loadDecorationsFromTilesets,
    undo,
    redo,
    canUndo,
    canRedo,
    historyVersion,
  ]);

  return (
    <EditModeContext.Provider value={value}>
      {children}
    </EditModeContext.Provider>
  );
}
